import React, { FC, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import {
  RichTextEditorProvider,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonEditLink,
  MenuControlsContainer,
  MenuDivider,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
} from 'mui-tiptap';
import { Box } from '@mui/material';

interface RichTextFieldProps {
  value: string;
  autoFocus?: boolean;
  placeholder?: string;
  onChange: (html: string) => void;
}

const RichTextLink = Link.extend({
  inclusive: false,
}).configure({
  autolink: true,
  openOnClick: false,
  HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
  protocols: ['http', 'https', 'mailto'],
});

export const RichTextField: FC<RichTextFieldProps> = ({ value, autoFocus, placeholder, onChange }) => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      Bold,
      Italic,
      Underline,
      RichTextLink,
      LinkBubbleMenuHandler,
      UndoRedo,
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    autofocus: autoFocus ? 'end' : false,
    // LinkBubbleMenu reads editor.storage.linkBubbleMenuHandler.state synchronously on render —
    // it needs the component to re-render on every transaction to see state changes.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value ?? '', { emitUpdate: false });
  }, [editor, value]);

  return (
    <Box
      sx={{
        '& .ProseMirror': {
          minHeight: 80,
          padding: '8px 12px',
          border: '1px solid rgba(0,0,0,0.23)',
          borderRadius: '4px',
          outline: 'none',
          '&:focus-within': { borderColor: 'primary.main' },
          '& p': { margin: 0 },
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            color: 'text.disabled',
            pointerEvents: 'none',
          },
        },
      }}>
      <RichTextEditorProvider editor={editor}>
        <MenuControlsContainer>
          <MenuButtonBold />
          <MenuButtonItalic />
          <MenuButtonUnderline />
          <MenuDivider />
          <MenuButtonEditLink />
        </MenuControlsContainer>
        <EditorContent editor={editor} />
        <LinkBubbleMenu />
      </RichTextEditorProvider>
    </Box>
  );
};
